import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import Dashboard from './components/dashboard/Dashboard';
import SupervisorDashboard from './components/supervisor/SupervisorDashboard';
import BrigadeList from './components/brigada/BrigadeList';
import RoleList from './components/Role/RoleList';
import UserList from './components/User/UserList';
import SectorList from './components/sectores/SectorList';
import CircunscripcionList from './components/Circunscripcion/CircunscripcionList';
import Mapa from './components/mapa/mapa';
// eslint-disable-next-line no-unused-vars
import Imagen from './components/imagenes/imagen';
import Login from './Login';
import Cookies from 'js-cookie';
import ErrorLayout from './shared/pages/ErrorLayout';
import apiService, { eventEmitter } from './shared/services/ApiService';
import socketService from './shared/services/socket';
import './App.css';

import {
  FaBars, FaChartBar, FaUserTie, FaUsers, FaUsersCog,
  FaLayerGroup, FaMapMarkedAlt, FaMap, FaSignOutAlt, FaFileAlt, FaExclamationCircle
} from 'react-icons/fa';
import ReportesPage from './components/reportes/ReportesPage';
import PublicLayout from './components/reporteCiudadano/PublicLayout';
import FormularioReporte from './components/reporteCiudadano/FormularioReporte';
import SeguimientoReporte from './components/reporteCiudadano/SeguimientoReporte';
import AdminReportesCiudadanos from './components/reporteCiudadano/AdminReportesCiudadanos';
import MapaPublico from './components/reporteCiudadano/MapaPublico';
import EmailConfig from './components/config/EmailConfig';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!Cookies.get('token'));
  const [userRole, setUserRole] = useState(Cookies.get('role') || null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Helper para verificar permisos
  const hasPermission = (...perms) => {
    return perms.some(p => userPermissions.includes(p));
  };

  useEffect(() => {
    const handleApiError = (status) => {
      // 400 y 403 son errores manejados por cada componente (validación, permisos)
      if (status === 400 || status === 403 || status === 404) {
        return;
      }
      window.location.href = `/error/${status}`;
    };

    eventEmitter.on('apiError', handleApiError);

    if (userRole) {
      setIsAuthenticated(true);
    }

    // Cargar permisos al iniciar
    if (isAuthenticated) {
      const storedPerms = Cookies.get('permissions');
      if (storedPerms) {
        try {
          setUserPermissions(JSON.parse(storedPerms));
        } catch (e) {
          // Si falla el parse, recargar desde API
          loadPermissions();
        }
      } else {
        loadPermissions();
      }
    }

    return () => {
      eventEmitter.off('apiError', handleApiError);
    };
  }, [userRole, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPermissions = async () => {
    try {
      const response = await apiService.get('/api/auth/me');
      const perms = response.data.permissions || [];
      setUserPermissions(perms);
      Cookies.set('permissions', JSON.stringify(perms));
    } catch (err) {
      console.error('Error cargando permisos:', err);
    }
  };

  useEffect(() => {
    let prevWidth = window.innerWidth;

    const handleResize = () => {
      const width = window.innerWidth;
      const wasMobile = prevWidth <= 768;
      const nowMobile = width <= 768;

      setIsMobile(nowMobile);

      if (!wasMobile && nowMobile) {
        setIsSidebarOpen(false);
      }

      prevWidth = width;
    };

    setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogin = (role, permissions) => {
    setIsAuthenticated(true);
    setUserRole(role);
    if (permissions) {
      setUserPermissions(permissions);
      Cookies.set('permissions', JSON.stringify(permissions));
    }
  };

  const handleLogout = () => {
    Cookies.remove('token');
    Cookies.remove('role');
    Cookies.remove('userId');
    Cookies.remove('permissions');
    localStorage.removeItem('token');

    // Desconectar socket
    socketService.disconnect();

    apiService.post('/api/auth/logout');
    setIsAuthenticated(false);
    setUserRole(null);
    setUserPermissions([]);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebarOnMobile = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Ruta standalone: mapa publico fuera de todo layout
  if (window.location.pathname === '/mapa-reportes') {
    return (
      <Router>
        <Routes>
          <Route path="/mapa-reportes" element={<MapaPublico />} />
          <Route path="*" element={<MapaPublico />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      {isAuthenticated ? (
        <div className="app-container">
          <aside className={`sidebar ${isSidebarOpen ? '' : 'collapsed'}`}>
            <button onClick={toggleSidebar} className="menu-button">
              <FaBars />
            </button>
            <h3 className="titulo-sidebar">Gestion de Brigadas</h3>
            <nav>
              <ul className="lista-sidebar">
                {hasPermission('dashboard:ver') && (
                  <li data-title="Estadisticas">
                    <Link to="/dashboard" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaChartBar /></span>
                      <span className="text">Estadisticas</span>
                    </Link>
                  </li>
                )}

                {hasPermission('supervisor:ver') && (
                  <li data-title="Supervisor">
                    <Link to="/supervisor" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaUserTie /></span>
                      <span className="text">Supervisor</span>
                    </Link>
                  </li>
                )}

                {hasPermission('mapa:ver') && (
                  <li data-title="Mapa">
                    <Link to="/mapa" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaMap /></span>
                      <span className="text">Mapa</span>
                    </Link>
                  </li>
                )}

                {hasPermission('brigadas:ver') && (
                  <li data-title="Brigadas">
                    <Link to="/brigadas" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaUsers /></span>
                      <span className="text">Brigadas</span>
                    </Link>
                  </li>
                )}

                {hasPermission('usuarios:ver') && (
                  <li data-title="Usuarios">
                    <Link to="/usuarios" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaUsersCog /></span>
                      <span className="text">Usuarios</span>
                    </Link>
                  </li>
                )}

                {hasPermission('roles:ver') && (
                  <li data-title="Roles">
                    <Link to="/roles" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaUsersCog /></span>
                      <span className="text">Roles</span>
                    </Link>
                  </li>
                )}

                {hasPermission('sectores:ver') && (
                  <li data-title="Sectores">
                    <Link to="/sectores" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaLayerGroup /></span>
                      <span className="text">Sectores</span>
                    </Link>
                  </li>
                )}

                {hasPermission('circunscripciones:ver') && (
                  <li data-title="Circunscripcion">
                    <Link to="/circunscripcion" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaMapMarkedAlt /></span>
                      <span className="text">Circunscripcion</span>
                    </Link>
                  </li>
                )}

                {hasPermission('reportes:ver') && (
                  <li data-title="Reportes">
                    <Link to="/reportes" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaFileAlt /></span>
                      <span className="text">Reportes</span>
                    </Link>
                  </li>
                )}

                {hasPermission('reportes_ciudadanos:ver', 'reportes_ciudadanos:ver_asignados') && (
                  <li data-title="Reportes Ciudadanos">
                    <Link to="/reportes-ciudadanos" onClick={closeSidebarOnMobile}>
                      <span className="icon"><FaExclamationCircle /></span>
                      <span className="text">Reportes Ciudadanos</span>
                    </Link>
                  </li>
                )}

                {hasPermission('configuracion_email:ver', 'configuracion_drive:ver') && (
                  <li data-title="Configuracion">
                    <Link to="/config/email" onClick={closeSidebarOnMobile}>
                      <span className="icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                      </span>
                      <span className="text">Configuracion</span>
                    </Link>
                  </li>
                )}

                <li data-title="Cerrar Sesion">
                  <button onClick={handleLogout}>
                    <span className="icon"><FaSignOutAlt /></span>
                    <span className="text">Cerrar Sesion</span>
                  </button>
                </li>
              </ul>
            </nav>
          </aside>

          {isMobile && isSidebarOpen && (
            <div className="mobile-menu-overlay" onClick={toggleSidebar}></div>
          )}

          {isMobile && !isSidebarOpen && (
            <button className="mobile-fab-menu" onClick={toggleSidebar} aria-label="Abrir menu">
              <FaBars />
            </button>
          )}

          <main className={`main-content ${isSidebarOpen ? '' : 'collapsed'}`}>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />

              <Route
                path="/supervisor"
                element={
                  hasPermission('supervisor:ver') ?
                  <SupervisorDashboard /> :
                  <Navigate to="/dashboard" />
                }
              />

              <Route
                path="/mapa"
                element={
                  hasPermission('mapa:ver') ?
                  <Mapa /> :
                  <Navigate to="/dashboard" />
                }
              />

              <Route path="/brigadas" element={hasPermission('brigadas:ver') ? <BrigadeList /> : <Navigate to="/dashboard" />} />
              <Route path="/usuarios" element={hasPermission('usuarios:ver') ? <UserList /> : <Navigate to="/dashboard" />} />
              <Route path="/roles" element={hasPermission('roles:ver') ? <RoleList /> : <Navigate to="/dashboard" />} />
              <Route path="/sectores" element={hasPermission('sectores:ver') ? <SectorList /> : <Navigate to="/dashboard" />} />
              <Route path="/circunscripcion" element={hasPermission('circunscripciones:ver') ? <CircunscripcionList /> : <Navigate to="/dashboard" />} />
              <Route path="/reportes" element={hasPermission('reportes:ver') ? <ReportesPage /> : <Navigate to="/dashboard" />} />

              <Route path="/reportes-ciudadanos" element={
                hasPermission('reportes_ciudadanos:ver', 'reportes_ciudadanos:ver_asignados')
                  ? <AdminReportesCiudadanos />
                  : <Navigate to="/dashboard" />
              } />

              <Route path="/config/email" element={
                hasPermission('configuracion_email:ver', 'configuracion_drive:ver')
                  ? <EmailConfig />
                  : <Navigate to="/dashboard" />
              } />

              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/error/:code" element={<ErrorLayout />} />
              <Route path="/login" element={<Navigate to="/dashboard" />} />

              {/* Rutas publicas accesibles tambien estando autenticado */}
              <Route path="/reporte-ciudadano" element={<PublicLayout><FormularioReporte /></PublicLayout>} />
              <Route path="/reporte-ciudadano/seguimiento" element={<PublicLayout><SeguimientoReporte /></PublicLayout>} />
              <Route path="/reporte-ciudadano/seguimiento/:code" element={<PublicLayout><SeguimientoReporte /></PublicLayout>} />
            </Routes>
          </main>
        </div>
      ) : (
        <div className="login-fullscreen">
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/error/:code" element={<ErrorLayout />} />
            {/* Rutas publicas sin autenticacion */}
            <Route path="/reporte-ciudadano" element={<PublicLayout><FormularioReporte /></PublicLayout>} />
            <Route path="/reporte-ciudadano/seguimiento" element={<PublicLayout><SeguimientoReporte /></PublicLayout>} />
            <Route path="/reporte-ciudadano/seguimiento/:code" element={<PublicLayout><SeguimientoReporte /></PublicLayout>} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </div>
      )}
    </Router>
  );
};

export default App;
